/// This component will wrap Apply Gift Card and Apply Rain Checks buttons as well as show applied credits
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import type { ReactNode } from "react";
import { useFragment } from "react-relay";

import { isAppliedPatronLoyaltyReward } from "@/features/Loyalty/components/PatronCheckoutLoyaltyRewards/PatronCheckoutLoyaltyRewards";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { GiftCardIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { RainIcon } from "@whoosh/design-system/src/components/icons_v2/weather";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import {
  isGiftCardModalOpenAtom,
  isRaincheckModalOpenAtom,
} from "../../atoms/stores";
import { useRemovePaymentGiftCardFromBooking } from "../../hooks/useRemovePaymentGiftCardFromBooking/useRemovePaymentGiftCardFromBooking";
import { useRemovePaymentRaincheckFromBooking } from "../../hooks/useRemovePaymentRaincheckFromBooking/useRemovePaymentRaincheckFromBooking";
import type {
  BookingCredits_booking$data,
  BookingCredits_booking$key,
} from "./__generated__/BookingCredits_booking.graphql";
import { BookingLoyaltyRewards } from "./components/BookingLoyaltyRewards/BookingLoyaltyRewards";
import type { BookingLoyaltyRewards_booking$key } from "./components/BookingLoyaltyRewards/__generated__/BookingLoyaltyRewards_booking.graphql";
import { BookingPaymentGiftCard } from "./components/BookingPaymentGiftCard/BookingPaymentGiftCard";
import { BookingPaymentRaincheck } from "./components/BookingPaymentRaincheck/BookingPaymentRaincheck";
import { BookingPromoCodeSection } from "./components/BookingPromoCode/BookingPromoCodeSection";
import { canChangeBookingPromoCode } from "./utils/canChangeBookingPromoCode";

type Props = {
  bookingRef: BookingCredits_booking$key | null;
  loyaltyBookingRef?: BookingLoyaltyRewards_booking$key | null;
};

const CreditsDivider = ({ testID }: { testID?: string }) => (
  <Box
    height={1}
    backgroundColor="outline-default"
    width="100%"
    testID={testID}
    style={{ marginTop: 1.5, marginBottom: 1.5 }}
  />
);

export const BookingCredits = ({ bookingRef, loyaltyBookingRef }: Props) => {
  const { couponCodes, patronLoyalty } = useFlags();
  const setShouldGiftCardModalBeOpen = useSetAtom(isGiftCardModalOpenAtom);
  const setShouldRainCheckModalBeOpen = useSetAtom(isRaincheckModalOpenAtom);
  const booking = useFragment(BookingCreditsFragmentString, bookingRef);

  const bookingHasRainchecks =
    (booking?.paymentRainchecks?.edges?.length ?? 0) > 0;
  const bookingHasBookingCredits =
    (booking?.paymentGiftCards?.edges?.length ?? 0) > 0 || bookingHasRainchecks;
  const bookingHasAppliedLoyaltyRewards =
    booking?.patronOrders?.edges?.[0]?.node?.squareLoyaltyRewards?.some(
      (reward) => isAppliedPatronLoyaltyReward(reward.status)
    ) ?? false;
  const bookingHasAppliedPromoCode =
    couponCodes === true && Boolean(booking?.appliedPromoCode);
  const bookingHasAppliedCards =
    bookingHasAppliedLoyaltyRewards || bookingHasBookingCredits;
  const hasAppliedItems = bookingHasAppliedCards || bookingHasAppliedPromoCode;

  const [handleRemovePaymentGiftCardFromBooking] =
    useRemovePaymentGiftCardFromBooking();

  const [handleRemovePaymentRaincheckFromBooking] =
    useRemovePaymentRaincheckFromBooking();

  const requiresPrePayment =
    booking?.paymentRequirements?.prePaymentRequirement === "FULL";

  const bookingCreditsNotNeeded =
    booking?.source === "STAFF" || booking?.status === "CONFIRMED";

  const trackEvent = useTrackEvent();

  const usedRaincheckIds =
    booking?.paymentRainchecks?.edges?.map((e) => e?.node?.id ?? "") ?? [];
  const usedGiftCardIds =
    booking?.paymentGiftCards?.edges?.map((edge) => edge?.node?.id ?? "") ?? [];

  const availableGiftCardEdges =
    booking?.ownerClient?.squareGiftCards?.edges?.filter(
      (edge) => !usedGiftCardIds.includes(edge?.node?.id ?? "")
    ) ?? [];
  const numberOfAvailableGiftCards = availableGiftCardEdges.length;
  const availableGiftCardBalance = availableGiftCardEdges.reduce(
    (total, edge) => total + (edge?.node?.balance ?? 0),
    0
  );

  const bookingFacilityId = booking?.startSlot?.agenda?.facility?.id;
  const today = new Date().toISOString().slice(0, 10);
  const redeemableRaincheckEdges =
    booking?.ownerClient?.rainchecks?.edges?.filter((edge) => {
      const raincheck = edge?.node;
      if (!raincheck) {
        return false;
      }

      const limitRedemptionToFacility =
        raincheck.facility.raincheckConfiguration.limitRedemptionToFacility;

      return (
        raincheck.squareGiftCard.status === "ACTIVE" &&
        (!raincheck.expirationDate || raincheck.expirationDate > today) &&
        (!limitRedemptionToFacility ||
          !bookingFacilityId ||
          raincheck.facility.id === bookingFacilityId)
      );
    }) ?? [];

  const availableRaincheckEdges = redeemableRaincheckEdges.filter(
    (edge) => !usedRaincheckIds.includes(edge?.node?.id ?? "")
  );
  const numberOfAvailableRainchecks = availableRaincheckEdges.length;
  const availableRaincheckBalance = availableRaincheckEdges.reduce(
    (total, edge) => total + (edge?.node?.squareGiftCard.balance ?? 0),
    0
  );
  const acceptsGiftCard =
    booking?.startSlot?.agenda?.facility?.acceptsGiftCard ?? true;
  const acceptsRaincheck =
    booking?.startSlot?.agenda?.facility?.acceptsRaincheck ?? true;
  const shouldShowGiftCardButton = acceptsGiftCard;
  const shouldShowRaincheckButton = acceptsRaincheck;
  const projectedCreditSummary = getProjectedCreditSummary(
    booking?.projectedCreditApplications ?? []
  );
  const creditSummary = patronLoyalty
    ? getPaidCreditSummary(booking?.orders?.edges ?? [])
    : getLegacyCreditSummary(booking?.orders?.edges ?? []);
  const giftCardApplications = [
    ...creditSummary.giftCardApplications,
    ...projectedCreditSummary.giftCardApplications,
  ];
  const raincheckAmount =
    creditSummary.raincheckAmount + projectedCreditSummary.raincheckAmount;
  const hasAppliedCreditPayments =
    raincheckAmount > 0 || giftCardApplications.length > 0;
  const canChangePromoCode = canChangeBookingPromoCode({
    source: booking?.source,
    orderStatuses:
      booking?.orders?.edges?.map((edge) => edge?.node?.status) ?? [],
  });
  const promoCodeSection = booking ? (
    <BookingPromoCodeSection
      bookingId={booking.id}
      canApply={canChangePromoCode}
      isEditable={canChangePromoCode}
      withDividers={!requiresPrePayment}
    />
  ) : null;

  if (!requiresPrePayment) {
    return promoCodeSection;
  }

  const appliedPromoCodeSection = bookingHasAppliedPromoCode
    ? promoCodeSection
    : null;
  const promoCodeActionSection = bookingHasAppliedPromoCode
    ? null
    : promoCodeSection;
  const showPromoCodeAction =
    Boolean(booking) &&
    !bookingHasAppliedPromoCode &&
    couponCodes === true &&
    canChangePromoCode;
  const showLoyaltyAction =
    Boolean(patronLoyalty) && booking?.status === "DRAFT";
  const showGiftCardOrRaincheckActions =
    !bookingCreditsNotNeeded &&
    (shouldShowGiftCardButton || shouldShowRaincheckButton);
  const showCreditAction =
    showPromoCodeAction || showLoyaltyAction || showGiftCardOrRaincheckActions;

  return (
    <VStack gap={4}>
      {hasAppliedCreditPayments ? (
        <>
          <CreditsDivider testID="credit-payments-divider-top" />

          <VStack gap={4} testID="applied-credit-payment-amounts">
            {raincheckAmount > 0 ? (
              <AppliedCreditPaymentRow
                label={t`Rainchecks`}
                amount={raincheckAmount}
                testID="applied-raincheck-amount"
              />
            ) : null}

            {giftCardApplications.map((application, index) => (
              <AppliedCreditPaymentRow
                key={`${application.squareGiftCard.id}-${index}`}
                label={t`Gift Card ending ${
                  application.squareGiftCard.ganLastFour ?? ""
                }`}
                amount={application.amount}
                testID="applied-gift-card-amount"
              />
            ))}
          </VStack>

          <CreditsDivider testID="credit-payments-divider-bottom" />
        </>
      ) : hasAppliedItems ? (
        <CreditsDivider />
      ) : null}
      {hasAppliedItems ? (
        <VStack gap={4} testID="applied-credit-cards">
          {appliedPromoCodeSection}

          {patronLoyalty ? (
            <BookingLoyaltyRewards
              bookingRef={loyaltyBookingRef ?? null}
              display="rewards"
              section="applied"
            />
          ) : null}

          {booking?.paymentRainchecks?.edges?.map((raincheck, index) => (
            <BookingPaymentRaincheck
              key={raincheck?.node?.id ?? index}
              raincheckRef={raincheck?.node ?? null}
              bookingStatus={booking?.status}
              onRemove={(raincheckId: string) => {
                handleRemovePaymentRaincheckFromBooking({
                  raincheckId,
                  bookingId: booking?.id ?? "",
                });
              }}
            />
          ))}

          {booking?.paymentGiftCards?.edges?.map((card, index) => (
            <BookingPaymentGiftCard
              key={card?.node?.id ?? index}
              squareGiftCardRef={card?.node ?? null}
              bookingStatus={booking?.status}
              onRemove={(giftCardId: string) => {
                handleRemovePaymentGiftCardFromBooking({
                  giftCardId,
                  bookingId: booking?.id ?? "",
                });
              }}
            />
          ))}
        </VStack>
      ) : null}
      {hasAppliedItems ? (
        <CreditsDivider testID="applied-credits-divider" />
      ) : null}
      {showCreditAction ? (
        <>
          <VStack gap={4} testID="credit-actions">
            {promoCodeActionSection}
            {showLoyaltyAction ? (
              <BookingLoyaltyRewards
                bookingRef={loyaltyBookingRef ?? null}
                display="rewards"
                section="action"
              />
            ) : null}
            {showGiftCardOrRaincheckActions ? (
              <>
                {shouldShowGiftCardButton ? (
                  <BookingCreditActionRow
                    accessibilityLabel={t`Apply Gift Card`}
                    testID="apply-gift-card-button"
                    onPress={() => {
                      trackEvent("BookingDetail.ApplyGiftCardClicked", {
                        bookingId: booking?.id ?? "",
                      });
                      setShouldGiftCardModalBeOpen(true);
                    }}
                    icon={<GiftCardIcon color="icon-main-primary" />}
                    label={t`Apply Gift Card`}
                    count={numberOfAvailableGiftCards}
                    balance={availableGiftCardBalance}
                  />
                ) : null}

                {shouldShowRaincheckButton ? (
                  <BookingCreditActionRow
                    accessibilityLabel={t`Apply Raincheck`}
                    testID="apply-rain-check-button"
                    onPress={() => setShouldRainCheckModalBeOpen(true)}
                    icon={<RainIcon color="icon-main-primary" />}
                    label={t`Apply Raincheck`}
                    count={numberOfAvailableRainchecks}
                    balance={availableRaincheckBalance}
                  />
                ) : null}
              </>
            ) : null}
          </VStack>
        </>
      ) : null}
      {showCreditAction || !hasAppliedItems ? (
        <CreditsDivider testID="credit-actions-divider" />
      ) : null}
    </VStack>
  );
};

type ProjectedCreditApplication = NonNullable<
  BookingCredits_booking$data["projectedCreditApplications"]
>[number];

type BookingOrderEdges = NonNullable<
  NonNullable<BookingCredits_booking$data["orders"]>["edges"]
>;

type GiftCardApplication = {
  amount: number;
  squareGiftCard: ProjectedCreditApplication["squareGiftCard"];
};

type CreditSummary = {
  giftCardApplications: GiftCardApplication[];
  raincheckAmount: number;
};

export const getProjectedCreditSummary = (
  applications: readonly ProjectedCreditApplication[]
) => ({
  giftCardApplications: applications.flatMap((application) =>
    application.paymentSource === "SQUARE_GIFT_CARD" && application.amount > 0
      ? [
          {
            amount: application.amount,
            squareGiftCard: application.squareGiftCard,
          },
        ]
      : []
  ),
  raincheckAmount: applications.reduce(
    (total, application) =>
      application.paymentSource === "RAINCHECK"
        ? total + application.amount
        : total,
    0
  ),
});

const getCreditSummary = (
  orderEdges: BookingOrderEdges,
  onlySettledPayments: boolean
): CreditSummary =>
  orderEdges.reduce<CreditSummary>(
    (summary, orderEdge) => {
      const order = orderEdge?.node;

      if (!order) {
        return summary;
      }

      if (
        onlySettledPayments &&
        order.status !== "FULLY_PAID" &&
        order.status !== "PARTIALLY_PAID" &&
        order.status !== "PARTIALLY_REFUNDED"
      ) {
        return summary;
      }

      return (order.orderPayments?.edges ?? []).reduce<CreditSummary>(
        (orderSummary, paymentEdge) => {
          const payment = paymentEdge?.node;
          const paymentIsSettled =
            payment?.status === "APPROVED" || payment?.status === "COMPLETED";

          const appliedAmount = Math.max(
            0,
            (payment?.amount ?? 0) - (payment?.refundedAmount ?? 0)
          );

          if (
            !payment ||
            (onlySettledPayments && !paymentIsSettled) ||
            appliedAmount <= 0
          ) {
            return orderSummary;
          }

          if (
            payment.paymentSource === "SQUARE_GIFT_CARD" &&
            payment.squareGiftCard
          ) {
            return {
              ...orderSummary,
              giftCardApplications: [
                ...orderSummary.giftCardApplications,
                {
                  amount: appliedAmount,
                  squareGiftCard: payment.squareGiftCard,
                },
              ],
            };
          }

          if (payment.paymentSource === "RAINCHECK") {
            return {
              ...orderSummary,
              raincheckAmount: orderSummary.raincheckAmount + appliedAmount,
            };
          }

          return orderSummary;
        },
        summary
      );
    },
    { giftCardApplications: [], raincheckAmount: 0 }
  );

export const getLegacyCreditSummary = (
  orderEdges: BookingOrderEdges
): CreditSummary => getCreditSummary(orderEdges, false);

export const getPaidCreditSummary = (
  orderEdges: BookingOrderEdges
): CreditSummary => getCreditSummary(orderEdges, true);

type AppliedCreditPaymentRowProps = {
  label: string;
  amount: number;
  testID: string;
};

const AppliedCreditPaymentRow = ({
  label,
  amount,
  testID,
}: AppliedCreditPaymentRowProps) => {
  const { currencySymbol } = useGetClubCurrency();

  return (
    <HStack justifyContent="space-between">
      <Text variant="mainBaseMedium">{label}</Text>
      <Text variant="mainBaseMedium" testID={testID}>
        {`-${currencySymbol}${ConvertCentsToDollars(amount)}`}
      </Text>
    </HStack>
  );
};

type BookingCreditActionRowProps = {
  accessibilityLabel: string;
  testID: string;
  onPress: () => void;
  icon: ReactNode;
  label: string;
  count: number;
  balance: number;
};

const BookingCreditActionRow = ({
  accessibilityLabel,
  testID,
  onPress,
  icon,
  label,
  count,
  balance,
}: BookingCreditActionRowProps) => {
  const { currencySymbol } = useGetClubCurrency();

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      testID={testID}
      onPress={onPress}
    >
      <HStack
        alignItems="center"
        justifyContent="space-between"
        gap={3}
        width="100%"
      >
        <HStack alignItems="center" gap={3}>
          <Box
            width={32}
            height={20}
            borderRadius="2"
            backgroundColor="surface-main-primary-subtlest"
            alignItems="center"
            justifyContent="center"
          >
            {icon}
          </Box>
          <HStack alignItems="center" gap={2}>
            <Text variant="mainBaseSemiBold" color="text-main-primary">
              {label}
            </Text>
            <Box
              width={18}
              height={18}
              borderRadius="6"
              backgroundColor="icon-main-primary"
              alignItems="center"
              justifyContent="center"
            >
              <Text variant="mainTiny" color="text-inverse">
                {count}
              </Text>
            </Box>
          </HStack>
        </HStack>
        <Text
          variant="mainBaseSemiBold"
          color="text-main-primary"
          testID={`${testID}-total`}
        >
          {`${currencySymbol}${ConvertCentsToDollars(balance)}`}
        </Text>
      </HStack>
    </Pressable>
  );
};

export const BookingCreditsFragmentString = graphql`
  fragment BookingCredits_booking on Booking
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    paymentRequirements {
      prePaymentRequirement
    }
    id
    source
    __typename
    appliedPromoCode {
      id
    }
    orders {
      edges {
        node {
          status
          orderPayments {
            edges {
              node {
                amount
                refundedAmount
                status
                paymentSource
                squareGiftCard {
                  id
                  ganLastFour
                }
              }
            }
          }
        }
      }
    }
    patronOrders: orders(first: 1) @include(if: $patronLoyalty) {
      edges {
        node {
          squareLoyaltyRewards {
            status
          }
        }
      }
    }
    startSlot {
      agenda {
        facility {
          id
          acceptsGiftCard
          acceptsRaincheck
        }
      }
    }
    ... on StandardBooking {
      totalDueAfterCredits
      status
      projectedCreditApplications @include(if: $patronLoyalty) {
        amount
        paymentSource
        squareGiftCard {
          id
          ganLastFour
        }
        raincheck {
          id
        }
      }
      ownerClient {
        ... on Customer {
          id
          squareGiftCards(active: true, hasNonZeroBalance: true) {
            edges {
              node {
                id
                balance
              }
            }
          }
          rainchecks(expired: false, hasNonZeroBalance: true) {
            edges {
              node {
                id
                expirationDate
                squareGiftCard {
                  balance
                  status
                }
                facility {
                  id
                  raincheckConfiguration {
                    limitRedemptionToFacility
                  }
                }
              }
            }
          }
        }
      }
      paymentGiftCards {
        edges {
          node {
            ...BookingPaymentGiftCard_SquareGiftCard
            id
            balance
            ganLastFour
          }
        }
      }

      paymentRainchecks {
        edges {
          node {
            ...BookingPaymentRaincheck_Raincheck
            id
          }
        }
      }
    }
    ... on LessonBooking {
      totalDueAfterCredits
      status
      projectedCreditApplications @include(if: $patronLoyalty) {
        amount
        paymentSource
        squareGiftCard {
          id
          ganLastFour
        }
        raincheck {
          id
        }
      }
      ownerClient {
        ... on Customer {
          id
          squareGiftCards(active: true, hasNonZeroBalance: true) {
            edges {
              node {
                id
                balance
              }
            }
          }
          rainchecks(expired: false, hasNonZeroBalance: true) {
            edges {
              node {
                id
                expirationDate
                squareGiftCard {
                  balance
                  status
                }
                facility {
                  id
                  raincheckConfiguration {
                    limitRedemptionToFacility
                  }
                }
              }
            }
          }
        }
      }
      paymentGiftCards {
        edges {
          node {
            ...BookingPaymentGiftCard_SquareGiftCard
            id
            balance
            ganLastFour
          }
        }
      }
      paymentRainchecks {
        edges {
          node {
            ...BookingPaymentRaincheck_Raincheck
            id
          }
        }
      }
    }
  }
`;
